import { useCallback, useEffect } from 'react';
import InvitationForm from '../../../ApplicationsTabPage/ListingApplicationsTab/ListingApplications/Invitation/InvitationForm';
import { Alert, Anchor, Paragraph, Toast, useToast } from '@zillow/constellation';
import { getSuccessDisplayName } from '../../../ApplicationsTabPage/ListingApplicationsTab/ListingApplications/Invitation/Invitation';
import LinkButton from '../../../../components/LinkButton';
import { ZTSS_DASHBOARD, ZTSS_ENTER_YOUR_ADDRESS } from '../../../../constants/paths';
import useQueryVariables from '../../../../hooks/useQueryVariables';
import useCreateCappsUser from '../../../../utils/apis/useCreateCappsUser';
import { URLS } from '../../../../constants/urls';
import ListingWizardStep from '../ListingWizardStep';
import { DetailedIconAddUser } from '@zillow/constellation';
import { useSyncListing } from '../../TenantScreeningServicesPage.utils';

import { useGetListingExistsWithRetry } from '../../../../utils/apis/useGetListingExistsWithRetry';
import {
  emitZtssContinueInviteARenterEvent,
  emitZtssInviteARenterEvent,
  emitZtssSendInviteEvent,
} from '../../../../../client/analytics/clickstreamEvents';
import { useListingUser } from '../../../../hooks/useListingUser';

export default function InviteARenterStep({ id, label }) {
  const { enqueueToast } = useToast();
  const { isSynced } = useSyncListing();
  const { propertyId } = useQueryVariables();
  const { mutate: createCappsUser, isPending: isCreatingCappsUser } = useCreateCappsUser();
  const { data: doesListingExistInListingHub } = useGetListingExistsWithRetry(propertyId);
  const { user, listing } = useListingUser();

  useEffect(createCappsUser, [createCappsUser]);

  const renderNextButton = useCallback(
    () => (
      <LinkButton
        to={`${ZTSS_DASHBOARD}?propertyId=${propertyId}`}
        buttonType="primary"
        onClick={() => emitZtssContinueInviteARenterEvent(listing, user)}
      >
        Applications dashboard
      </LinkButton>
    ),
    [propertyId],
  );
  const renderPrevButton = useCallback(
    ({ buttonProps }) => (
      <LinkButton to={ZTSS_ENTER_YOUR_ADDRESS} {...buttonProps}>
        Back
      </LinkButton>
    ),
    [],
  );

  useEffect(() => {
    if (doesListingExistInListingHub) {
      emitZtssInviteARenterEvent(listing, user);
    }
  }, [doesListingExistInListingHub]);

  return (
    <ListingWizardStep
      id={id}
      label={label}
      meta={{
        renderNextButton,
        renderPrevButton,
      }}
      isLoading={isCreatingCappsUser || !doesListingExistInListingHub || !isSynced}
      icon={<DetailedIconAddUser />}
      title="Send an application request"
      description={[
        "Enter the information of the primary applicant you'd like to screen.",
        'You only need to send one application per group of renters. The applicant you list below can add any co-applicants.',
      ]}
      render={() => {
        if (!doesListingExistInListingHub) {
          return <Alert appearance="error" body="An error has occurred" />;
        }
        return (
          <>
            <InvitationForm
              onSubmit={(values) => {
                const displayName = getSuccessDisplayName(values);
                emitZtssSendInviteEvent(listing, user);
                enqueueToast(
                  <Toast
                    id="invite-applicant-toast-success"
                    appearance="success"
                    body={`You have invited ${displayName} to apply to your rental.`}
                  />,
                );
              }}
              marginBottom="md"
            />
            <Paragraph fontType="finePrint">
              By enabling or sharing applications, you agree to comply with our{' '}
              <Anchor href={URLS.TERMS_OF_USE} target="_blank">
                Terms of Use
              </Anchor>
              ,{' '}
              <Anchor href={URLS.RENTAL_USER_TERMS} target="_blank">
                Rental User Terms
              </Anchor>
              , and{' '}
              <Anchor href={URLS.RESPECTFUL_RENTING_PLEDGE} target="_blank">
                Respectful Renting Pledge
              </Anchor>
              .
            </Paragraph>
          </>
        );
      }}
    />
  );
}
